import { addDays } from '~/calculations/calendar'
import { type StrengthSetInput, weeklyFigures } from '~/calculations/estimated1RM'
import { RECOVERY_ITEMS, type RecoveryCheck } from '~/models/recoveryCheck'
import type { WeekStartDay } from '~/models/settings'
import type { WeeklyReviewRecord } from '~/models/weeklyReview'
import type { RecoveryFacts, StrengthFacts } from '~/rules/facts'
import type { RulesThresholds } from '~/rules/thresholds'
import type { CalendarDate } from '~/types/dates'

/**
 * Recovery and strength, summarised from stored history for the engine.
 *
 * Both need weeks the engine may not read: the rules layer is pure and touches
 * no storage, and a four-week comparison is history by definition. So the
 * counting happens here and the engine receives plain numbers — which is the
 * same split `waistFrom` already follows.
 *
 * **Nothing here decides anything.** These produce observations; the rulesets
 * compare them against `rules/thresholds.ts`. Moving the comparison in here
 * would put a coaching threshold outside the one file that is allowed to hold
 * one.
 */

const DAYS_IN_WEEK = 7

/** The mean of the five required items. Optional items are excluded (D-066). */
export function recoveryMean(check: RecoveryCheck): number {
  const total = RECOVERY_ITEMS.reduce((sum, item) => sum + (check.ratings[item] ?? 0), 0)
  return total / RECOVERY_ITEMS.length
}

/** The worst of the five required items this week. */
export function recoveryLowest(check: RecoveryCheck): number {
  return Math.min(...RECOVERY_ITEMS.map((item) => check.ratings[item] ?? 0))
}

/** How many trailing weeks the recovery and strength comparisons look back. */
const COMPARISON_WEEKS = 4

/**
 * Recovery for the current week, against its own trailing four-week average.
 *
 * Weeks with no recovery check are omitted from the average rather than counted
 * as zero — a week the user did not rate is not a week of terrible recovery.
 * This is the same rule the trailing weight average follows (D-036).
 */
export function summariseRecovery(
  reviews: readonly WeeklyReviewRecord[],
  currentWeekStart: CalendarDate,
): RecoveryFacts {
  const byWeek = new Map(reviews.map((review) => [review.weekStartDate as string, review]))
  const current = byWeek.get(currentWeekStart as string)?.recovery

  if (current === undefined) {
    return {
      available: false,
      lowestItemScore: undefined,
      meanScore: undefined,
      meanDropFromFourWeekAverage: undefined,
    }
  }

  const meanScore = recoveryMean(current)

  const earlier: number[] = []
  let week = addDays(currentWeekStart, -DAYS_IN_WEEK)
  for (let step = 0; step < COMPARISON_WEEKS; step += 1) {
    const check = byWeek.get(week as string)?.recovery
    if (check !== undefined) earlier.push(recoveryMean(check))
    week = addDays(week, -DAYS_IN_WEEK)
  }

  const average =
    earlier.length === 0
      ? undefined
      : earlier.reduce((sum, value) => sum + value, 0) / earlier.length

  return {
    available: true,
    lowestItemScore: recoveryLowest(current),
    meanScore,
    meanDropFromFourWeekAverage: average === undefined ? undefined : average - meanScore,
  }
}

export interface LiftHistory {
  readonly liftId: string
  readonly sets: readonly StrengthSetInput[]
}

/**
 * Strength decline, counted per week across every tracked lift (D-066, D-069).
 *
 * A lift counts as declining in a week when its figure sits at or below the
 * approved percentage under **its own** trailing four-week average. The week
 * counts when enough lifts decline together, and the run is how many such weeks
 * end at the current one.
 *
 * A lift with no figure in a week is **not** counted as declining. Missing is
 * not decline, and treating it as one would let a skipped session read as lost
 * strength — the same error as scoring a missing weigh-in as zero.
 */
export function summariseStrength(
  lifts: readonly LiftHistory[],
  currentWeekStart: CalendarDate,
  weekStartDay: WeekStartDay,
  thresholds: RulesThresholds,
): StrengthFacts {
  if (lifts.length === 0) {
    return {
      liftsTracked: 0,
      liftsDecliningThisWeek: 0,
      consecutiveWeeksDeclining: 0,
      unavailable: true,
    }
  }

  const declineFraction = thresholds.strength.declinePercentBelowFourWeekAverage / 100

  /** Lifts declining in the week starting on `week`. */
  const decliningIn = (week: CalendarDate): { declining: number; comparable: number } => {
    let declining = 0
    let comparable = 0

    for (const lift of lifts) {
      const figures = weeklyFigures(lift.sets, weekStartDay, thresholds.strength)
      const current = figures.find((figure) => figure.weekStartDate === week)
      if (current?.status !== 'present') continue

      const earlier = figures
        .filter((figure) => figure.weekStartDate < week && figure.status === 'present')
        .slice(-COMPARISON_WEEKS)
      if (earlier.length === 0) continue

      const average =
        earlier.reduce((sum, figure) => sum + (figure.estimateKg as number), 0) / earlier.length

      comparable += 1
      if ((current.estimateKg as number) <= average * (1 - declineFraction)) declining += 1
    }

    return { declining, comparable }
  }

  const thisWeek = decliningIn(currentWeekStart)

  let run = 0
  let week = currentWeekStart
  /* Bounded by the duration the rule actually needs plus a margin, so a long
     history cannot turn this into an unbounded walk backwards. */
  const maxLookback = thresholds.strength.consecutiveWeeksRequired + COMPARISON_WEEKS
  for (let step = 0; step < maxLookback; step += 1) {
    if (decliningIn(week).declining < thresholds.strength.liftsRequired) break
    run += 1
    week = addDays(week, -DAYS_IN_WEEK)
  }

  return {
    liftsTracked: lifts.length,
    liftsDecliningThisWeek: thisWeek.declining,
    consecutiveWeeksDeclining: run,
    unavailable: thisWeek.comparable === 0,
  }
}
